import { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Container,
  Content,
  Button,
  Schema,
  Loader,
  SelectPicker,
  Input,
  FlexboxGrid,
  DatePicker,
  IconButton,
} from "rsuite";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { updateIn } from "json-immut";
import { StringType, NumberType } from "schema-typed";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { FollowupServices } from "../../../core/services/FollowupServices";
import { get } from "lodash/fp";
import { Edit, Trash, More } from "@rsuite/icons";
import "./Followups.css";
import {
  dateFormatObj,
  followupForObj,
  followupForObjArr,
  followupStatusObj,
  followupStatusObjArr,
  messageTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import React from "react";
import {
  calenderDateToDB,
  dbDateToCalender,
  displayDateTimeField,
  isEmptyVal,
  isNotEmpty,
  isNotNull,
} from "../../../utils/helpers";

const Textarea = React.forwardRef((props: any, ref: any) => (
  <Input {...props} as="textarea" ref={ref} />
));
const textareaStyle = {
  width: "300px", // Set the desired width here
};

const commentTextareaStyle = {
  width: "500 px", // Set the desired width here
};

const Followup = ({
  openModal,
  setOpenModal,
  rowData,
  followupId = 0,
  onFollowup,
  permission,
}: {
  openModal: boolean;
  setOpenModal: any;
  rowData: any;
  followupId?: number;
  onFollowup?: Function;
  permission?: any;
}) => {
  const { t } = useTranslation();
  const formRef = useRef<any>();

  const [openCommentDeleteModal, setOpenCommentDeleteModal] = useState(false);
  const [updateCommentId, setUpdateCommentId] = useState<number>(0);
  const [deleteCommentId, setDeleteCommentId] = useState<number>(0);
  const [commentsList, setCommentsList] = useState<any[]>([]);
  const [formError, setFormError] = useState<any>({});
  const initFollowupFormData: any = {
    customer_id: rowData?.customer_id,
    title: "",
    follow_up_for: followupForObj.order_booking.value,
    follow_up_status: followupStatusObj.pending.value,
    follow_up_date: null,
    notes: "",
  };
  const initCommentFormData: any = {
    comment: "",
  };
  const [formData, setFormData] = useState<any>(initFollowupFormData);
  const [commentFormData, setCommentFormData] =
    useState<any>(initCommentFormData);
  const [loading, setLoading] = useState(false);

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }
  function handleCommentFormChanges(data: any) {
    let form = updateIn(commentFormData, [data.path], data.value);
    setCommentFormData(form);
  }

  const formModel = Schema.Model({
    notes: StringType(),
    title: StringType(),
  });

  const commentFormModel = Schema.Model({
    comment: StringType(),
  });

  const handleDateChange = (date: any, frmIndex: any) => {
    let frmObj = { ...formData };
    frmObj[frmIndex as keyof typeof frmObj] = date;
    setFormData({ ...frmObj });
  };

  useEffect(() => {
    if (isEmptyVal(rowData?.customer_id)) {
      UtilityServices.showToaster({
        type: "warning",
        message: t("REQUIRED", {
          field: t("CUSTOMER").toLowerCase() + " " + t("ID").toLowerCase(),
        }),
      });
      setOpenModal("");
    }
    init();
  }, []);

  async function init() {
    try {
      if (followupId) {
        setLoading(true);

        const response: any = await FollowupServices.viewFollowup(+followupId);
        const respData = get("data.data", response);
        let obj = { ...formData };
        const ignoreKeys: string[] = ["comments", "notes"];
        Object.keys(obj).map((formKey) => {
          if (!ignoreKeys.includes(formKey)) {
            if (formKey == "follow_up_date") {
              obj["follow_up_date"] = dbDateToCalender(
                respData["follow_up_date"]
              );
            } else if (respData[formKey]) {
              obj[formKey] = respData[formKey];
            }
          }
        });
        setFormData({ ...obj });
        if (respData?.comments.length > 0) {
          setCommentsList(respData["comments"]);
        }
        setFormData({ ...obj });
        setLoading(false);
      } else {
      }
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
      setOpenModal("");
    }
  }

  const onFollowupSubmit = (val: any) => {
    const form = new FormData();

    for (var key in formData) {
      if (key == "follow_up_date") {
        form.append("follow_up_date", calenderDateToDB(formData[key]));
      } else if (isNotNull(formData[key])) {
        form.append(key, formData[key]);
      }
    }

    if (isEmptyVal(form.get("notes")) && isEmptyVal(followupId)) {
      UtilityServices.showToaster({
        type: "warning",
        message: t("REQUIRED", {
          field: t("NOTE").toLowerCase(),
        }),
      });
      return;
    }

    if (isEmptyVal(formData["follow_up_date"])) {
      UtilityServices.showToaster({
        type: "warning",
        message: t("REQUIRED", {
          field: t("FOLLOWUP").toLowerCase() + " " + t("DATE").toLowerCase(),
        }),
      });
      return;
    }

    if (followupId) {
      form.delete("customer_id");
      saveFollowup(form, followupId);
    } else {
      saveFollowup(form);
    }
  };

  const onCommentSubmit = (val: any) => {
    if (isEmptyVal(commentFormData.comment)) {
      UtilityServices.showToaster({
        type: "warning",
        message: t("REQUIRED", {
          field: t("COMMENT").toLowerCase(),
        }),
      });
      return;
    }
    if (updateCommentId) {
      saveComment(commentFormData, updateCommentId);
    }
  };

  async function saveComment(form: any, id?: number) {
    try {
      setLoading(true);
      let response: any;
      if (id) {
        response = await FollowupServices.updateComment(+id, form);
      }
      showFollowUpAfterCommentUpdate(response);
      setLoading(false);
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function deleteComment(id?: number) {
    try {
      setLoading(true);
      let response: any;
      if (id) {
        response = await FollowupServices.deleteComment(+id);
      }
      showFollowUpAfterCommentDelete(response);
      setLoading(false);
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }

  function showFollowUpAfterCommentUpdate(response: any) {
    setCommentsList(get("data.data.comments", response));
    setUpdateCommentId(0);
    setCommentFormData(initCommentFormData);
  }

  function showFollowUpAfterCommentDelete(response: any) {
    setCommentsList(get("data.data.comments", response));
    setDeleteCommentId(0);
  }

  async function saveFollowup(form: any, id?: number) {
    try {
      setLoading(true);
      let response: any;
      if (id) {
        response = await FollowupServices.updateFollowup(+id, form);
      } else {
        response = await FollowupServices.createFollowup(form);
      }
      setLoading(false);
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
      if (onFollowup !== undefined) {
        onFollowup();
      }
      setOpenModal("");
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }
  return (
    <div className="modal-container">
      <Modal
        open={openModal}
        backdrop={true}
        onClose={(e: any) => {
          setOpenModal("");
        }}
        size="md"
      >
        {loading && (
          <Loader
            style={{ zIndex: 9999 }}
            size="lg"
            backdrop
            content={t("LOADING")}
            vertical
          />
        )}
        <Modal.Header>
          <h4>
            <b>
              {t("FOLLOWUP")} {t("FOR")}
            </b>{" "}
            {rowData?.name}
            {" ("}
            {rowData?.mobile}
            {" )"}
          </h4>
          <label>
            {" "}
            (<b>{t("NOTE")}</b> : {t("THERE")} {t("ARE").toLowerCase()}{" "}
            <b>{rowData?.pending_follow_ups}</b> {t("OPEN").toLowerCase()}{" "}
            {t("TICKETS").toLowerCase()} {t("FOR").toLowerCase()}{" "}
            {t("THIS").toLowerCase()} {t("CUSTOMER").toLowerCase()})
          </label>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Content className="edit-cnt">
              <div id="form">
                {!updateCommentId && (
                  <div id="followup_form">
                    <Form
                      layout="inline"
                      ref={formRef}
                      model={formModel}
                      formError={formError}
                      formDefaultValue={formData}
                      onCheck={(e: any) => {
                        setFormError(e);
                      }}
                      onSubmit={(e: any) => {
                        if (e) {
                          onFollowupSubmit(e);
                        }
                      }}
                    >
                      <div className="d-flex">
                        <FlexboxGrid justify="space-between">
                          <FlexboxGrid.Item>
                            <Form.Group controlId="follow_up_for">
                              <Form.ControlLabel>
                                {t("FOLLOWUP")} {t("FOR")}
                              </Form.ControlLabel>
                              <SelectPicker
                                data={followupForObjArr}
                                valueKey="value"
                                labelKey="label"
                                disabled={followupId ? true : false}
                                onSelect={(value, item) => {
                                  handleFormChanges({
                                    path: "follow_up_for",
                                    value,
                                  });
                                }}
                                onClean={(e: any) => {
                                  handleFormChanges({
                                    path: "follow_up_for",
                                    value: "",
                                  });
                                }}
                                name="follow_up_for"
                                value={formData?.follow_up_for}
                                size="sm"
                              />
                            </Form.Group>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item>
                            <Form.Group controlId="title">
                              <Form.ControlLabel>
                                {t("TITLE")}
                              </Form.ControlLabel>
                              <Form.Control
                                name="title"
                                key="title"
                                value={formData?.title}
                                onChange={(value, item) => {
                                  handleFormChanges({
                                    path: "title",
                                    value,
                                  });
                                }}
                              />
                            </Form.Group>
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item>
                            <Form.Group controlId="follow_up_date">
                              <Form.ControlLabel>
                                {t("NEXT")} {t("FOLLOWUP")} {t("DATE")}
                              </Form.ControlLabel>
                              <DatePicker
                                value={formData?.follow_up_date}
                                format={dateFormatObj.calendar_d_m_y_h_m}
                                name="follow_up_date"
                                id="follow_up_date"
                                onChange={(e: any) => {
                                  handleDateChange(e, "follow_up_date");
                                }}
                              />
                            </Form.Group>
                          </FlexboxGrid.Item>
                          <FlexboxGrid.Item>
                            <Form.Group controlId="follow_up_status">
                              <Form.ControlLabel>
                                {t("FOLLOWUP")} {t("STATUS")}
                              </Form.ControlLabel>
                              <SelectPicker
                                data={followupStatusObjArr}
                                valueKey="value"
                                labelKey="label"
                                onSelect={(value, item) => {
                                  handleFormChanges({
                                    path: "follow_up_status",
                                    value,
                                  });
                                }}
                                onClean={(e: any) => {
                                  handleFormChanges({
                                    path: "follow_up_status",
                                    value: "",
                                  });
                                }}
                                name="follow_up_status"
                                value={formData?.follow_up_status}
                                size="sm"
                              />
                            </Form.Group>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                        <FlexboxGrid
                          justify="space-between"
                          className="follow-note"
                        >
                          <FlexboxGrid.Item>
                            <Form.Group controlId="notes">
                              <Form.ControlLabel>{t("NOTE")}</Form.ControlLabel>
                              <Form.Control
                                name="notes"
                                rows={5}
                                style={textareaStyle}
                                accepter={Textarea}
                                onChange={(value: string, item: any) => {
                                  handleFormChanges({
                                    path: "notes",
                                    value,
                                  });
                                }}
                              />
                            </Form.Group>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                      </div>
                      <div className="usr-form-btn mt-0 mb-4">
                        <Form.Group>
                          <Button appearance="primary" type="submit">
                            {followupId ? t("UPDATE") : t("CREATE")}
                          </Button>
                          <Button
                            style={{ marginLeft: "10px" }}
                            onClick={(e: any) => {
                              setOpenModal("");
                            }}
                            appearance="primary"
                            color="red"
                            type="reset"
                          >
                            {t("CLOSE")}
                          </Button>
                        </Form.Group>
                      </div>
                    </Form>
                  </div>
                )}

                {updateCommentId > 0 && (
                  <div id="comment_form">
                    <Form
                      layout="inline"
                      model={commentFormModel}
                      formError={formError}
                      formDefaultValue={commentFormData}
                      onCheck={(e: any) => {
                        setFormError(e);
                      }}
                      onSubmit={(e: any) => {
                        if (e) {
                          onCommentSubmit(e);
                        }
                      }}
                    >
                      <div className="d-flex">
                        <FlexboxGrid justify="space-between">
                          <FlexboxGrid.Item>
                            <Form.Group controlId="comment">
                              <Form.ControlLabel>
                                {t("COMMENT")}
                              </Form.ControlLabel>
                              <Form.Control
                                name="comment"
                                rows={5}
                                style={commentTextareaStyle}
                                accepter={Textarea}
                                onChange={(value: string, item: any) => {
                                  handleCommentFormChanges({
                                    path: "comment",
                                    value,
                                  });
                                }}
                                value={commentFormData?.comment}
                              />
                            </Form.Group>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                      </div>
                      <div className="usr-form-btn mt-0 mb-4">
                        <Form.Group>
                          <Button appearance="primary" type="submit">
                            {t("UPDATE")}
                          </Button>
                          <Button
                            style={{ marginLeft: "10px" }}
                            onClick={(e: any) => {
                              setUpdateCommentId(0);
                              setCommentFormData(initCommentFormData);
                            }}
                            appearance="primary"
                            color="red"
                            type="button"
                          >
                            {t("CANCEL")}
                          </Button>
                        </Form.Group>
                      </div>
                    </Form>
                  </div>
                )}
              </div>
              {followupId > 0 && (
                <div id="comment">
                  {commentsList.map((item: any, index: number) => {
                    return (
                      <div className="comment-bx d-flex">
                        <div>
                          <b>
                            {t("COMMENT") +
                              " " +
                              t("BY") +
                              ": " +
                              item.commentby.name +
                              " " +
                              t("ON") +
                              " : " +
                              displayDateTimeField(item.created_at)}
                          </b>
                          <p>{item.comment}</p>
                        </div>
                        <div>
                          {UtilityServices.hasPermissionOnComment(item) && (
                            <>
                              <IconButton
                                appearance="subtle"
                                onClick={(e: any) => {
                                  setUpdateCommentId(item.id);
                                  setCommentFormData({ comment: item.comment });
                                }}
                                title={t("EDIT")}
                                icon={<Edit />}
                              />
                              <IconButton
                                onClick={() => {
                                  setDeleteCommentId(item.id);
                                  setOpenCommentDeleteModal(true);
                                }}
                                icon={<Trash />}
                                title={t("DELETE")}
                                size="md"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Content>
          </Container>
        </Modal.Body>
      </Modal>
      <AlertModal
        open={openCommentDeleteModal}
        handleClose={(e: any) => {
          if (e == true) {
            deleteComment(deleteCommentId);
            setOpenCommentDeleteModal(false);
          } else {
            setOpenCommentDeleteModal(false);
            setDeleteCommentId(0);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("COMMENT").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
    </div>
  );
};

export default Followup;
